import { useEffect, useMemo, useRef, useState } from "react"
import { IntRange } from "../tile/types"
import acceptableFramerate from "../utils/acceptableFramerate"

function easeInOutQuint(x: number) {
  return x < 0.5 ? 16 * x * x * x * x * x : 1 - Math.pow(-2 * x + 2, 5) / 2
}

function easeOutExpo(x: number) {
  return x === 1 ? 1 : 1 - Math.pow(2, -10 * x)
}

function easeOutCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
  }

function easeInQuart(x: number) {
  return x * x * x * x
}

function easeOutElastic(x: number) {
  const c4 = (2 * Math.PI) / 3

  return x === 0
    ? 0
    : x === 1
    ? 1
    : Math.pow(2, -12 * x) * Math.sin((x * 4 - 0.6) * c4) + 1
}

function easeInSine(x: number): number {
  return 1 - Math.cos((x * Math.PI) / 2)
}

function linear(x: number): number {
  return x + 1
}

const easings = {
  easeInSine,
  easeInOutQuint,
  easeOutExpo,
  easeInQuart,
  easeOutElastic,
  easeOutCubic,
  linear
}

type Speed = IntRange<0, 11>

function useEase({
  start,
  end,
  ease,
  speed,
  manualTrigger = false,
  fillMode = "",
  onEnd = () => null
}: {
  start: number | undefined
  end: number | undefined
  ease: keyof typeof easings
  speed: Speed
  manualTrigger?: boolean
  fillMode?: "persist" | ""
  onEnd?: () => void | unknown
}) {
  const transformedSpeed = (speed * 0.099) / 10 + 0.01

  const easingFunction = easings[ease]

  const DEFAULT_PROGRESS = {
    increment: 0.01,
    ease: start && end ? start + (end - start) * easingFunction(0.01) : 0.01
  }

  const [animationProgress, setAnimProgress] = useState(DEFAULT_PROGRESS)

  const shouldAnim = useMemo(() => acceptableFramerate(40), [])

  const animationFrameId = useRef(null)

  function startHandler() {
    if (animationProgress.increment > 1) return
    if (typeof start === "undefined" && typeof end === "undefined") return

    if (shouldAnim()) {
      setAnimProgress(({ increment }) => {
        return {
          increment: increment + transformedSpeed,
          // Use simple linear interpolation to animate between two points
          ease: start + (end - start) * easingFunction(increment)
        }
      })
    }

    animationFrameId.current = requestAnimationFrame(startHandler)
  }

  useEffect(() => {
    if (!manualTrigger) startHandler()
    return () => cancelAnimationFrame(animationFrameId.current)
  }, [start, end])

  useEffect(() => {
    if (animationProgress.increment >= 1) {
      cancelAnimationFrame(animationFrameId.current)
      onEnd()
      if (fillMode !== "persist") setAnimProgress(DEFAULT_PROGRESS)
    }
  }, [animationProgress.increment])

  return { animationProgress, start: startHandler }
}

export default useEase
